import React, { useContext } from 'react';
import { useTheme } from '../ThemeContext';
import { EarningsCalendarItem, EarningsService } from '../../services/EarningsService';
import { useState } from 'react';
import { SmartTranscript } from './SmartTranscript';
import { Building2, FileText, Lock } from 'lucide-react';
import { UserContext } from '../../context/UserContext';

interface EarningsCardProps {
    item: EarningsCalendarItem;
    viewType?: 'simple' | 'advanced';
}

export const EarningsCard: React.FC<EarningsCardProps> = ({ item, viewType = 'advanced' }) => {
    const { theme } = useTheme();
    const { userData } = useContext(UserContext) || {};
    const hasAccess = !!userData;
    const isDark = theme === 'dark';
    const [showTranscript, setShowTranscript] = useState(false);
    const [transcriptData, setTranscriptData] = useState<{ content: string; year: number; quarter: string } | null>(null);
    const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);

    const handleShowTranscript = async () => {
        if (transcriptData) {
            setShowTranscript(true);
            return;
        }

        setIsLoadingTranscript(true);
        try {
            const data = await EarningsService.getRecentEarningsTranscript(item.symbol);
            setTranscriptData(data);
            setShowTranscript(true);
        } catch (error) {
            console.error('Failed to load transcript', error);
            // Fallback to sample if API fails (optional, or show error)
            // setTranscriptData({ content: SAMPLE_TRANSCRIPT, year: 2025, quarter: '3' });
            // setShowTranscript(true);
        } finally {
            setIsLoadingTranscript(false);
        }
    };

    const parseValue = (val: any): number | null => {
        if (val === null || val === undefined || val === '') return null;
        const num = Number(val);
        return isNaN(num) ? null : num;
    };

    const formatCurrency = (val: any) => {
        const num = parseValue(val);
        if (num === null) return 'N/A';

        const absVal = Math.abs(num);
        const sign = num < 0 ? '-' : '';
        if (absVal >= 1.0e9) {
            return `${sign}$${(absVal / 1.0e9).toFixed(2)}B`;
        } else if (absVal >= 1.0e6) {
            return `${sign}$${(absVal / 1.0e6).toFixed(2)}M`;
        }
        return `${sign}$${absVal.toLocaleString()}`;
    };

    const formatEPS = (val: any) => {
        const num = parseValue(val);
        if (num === null) return 'N/A';
        return num < 0 ? `-$${Math.abs(num).toFixed(2)}` : `$${num.toFixed(2)}`;
    };

    // Determine beat/miss status with safe parsing
    const isBeat = (actual: any, estimated: any) => {
        const act = parseValue(actual);
        const est = parseValue(estimated);
        return act !== null && est !== null && act > est;
    };

    const isMiss = (actual: any, estimated: any) => {
        const act = parseValue(actual);
        const est = parseValue(estimated);
        return act !== null && est !== null && act < est;
    };

    const epsBeat = isBeat(item.epsActual, item.epsEstimated);
    const epsMiss = isMiss(item.epsActual, item.epsEstimated);
    const revBeat = isBeat(item.revenueActual, item.revenueEstimated);
    const revMiss = isMiss(item.revenueActual, item.revenueEstimated);

    // Helper for color classes to ensure they apply
    const getColorClass = (beat: boolean, miss: boolean) => {
        if (beat) return 'text-green-500 font-bold';
        if (miss) return 'text-red-500 font-bold';
        return isDark ? 'text-gray-300' : 'text-gray-700';
    };

    if (viewType === 'simple') {
        return (
            <div className={`p-2 border rounded-lg transition-all duration-200 hover:shadow-md h-full flex flex-col items-center justify-center ${isDark
                ? 'bg-theme-secondary border-theme-tertiary hover:border-theme-accent'
                : 'bg-white border-gray-200 hover:border-blue-300'
                }`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden mb-1 ${isDark ? 'bg-white' : 'bg-gray-50'
                    }`}>
                    {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.symbol} className="w-full h-full object-contain p-1" />
                    ) : (
                        <Building2 size={16} className="text-gray-400" />
                    )}
                </div>
                <div className={`font-bold text-xs mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {item.symbol}
                </div>
                <div className={`text-[8px] truncate w-full text-center leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {item.companyName}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={`p-2.5 border rounded-lg transition-all duration-200 hover:shadow-md ${isDark
                ? 'bg-theme-secondary border-theme-tertiary hover:border-theme-accent'
                : 'bg-white border-gray-200 hover:border-blue-300'
                }`}>
                <div className="flex gap-2 items-center">
                    {/* Left Column: Logo & Name */}
                    <div className="flex flex-col items-center min-w-[70px]">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden mb-1.5 ${isDark ? 'bg-white' : 'bg-gray-50'
                            }`}>
                            {item.imageUrl ? (
                                <img src={item.imageUrl} alt={item.symbol} className="w-full h-full object-contain p-1.5" />
                            ) : (
                                <Building2 size={20} className="text-gray-400" />
                            )}
                        </div>
                        <div className={`font-bold text-sm mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {item.symbol}
                        </div>
                        <div className={`text-[9px] truncate w-full text-center leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {item.companyName}
                        </div>
                    </div>

                    {/* Right Column: EPS & Revenue */}
                    <div className="flex-1 flex flex-col gap-1.5">
                        {/* EPS */}
                        <div>
                            <div className={`text-[10px] font-semibold mb-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                EPS
                            </div>
                            <div className="flex items-baseline gap-1 flex-wrap">
                                {hasAccess ? (
                                    <>
                                        <span className={`text-sm ${getColorClass(epsBeat, epsMiss)}`} style={{ color: epsBeat ? '#22c55e' : epsMiss ? '#ef4444' : undefined }}>
                                            {formatEPS(item.epsActual)}
                                        </span>
                                        {item.epsEstimated !== null && (
                                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                / {formatEPS(item.epsEstimated)}
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex items-center gap-1 text-gray-400">
                                        <Lock size={12} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Revenue */}
                        <div>
                            <div className={`text-[10px] font-semibold mb-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Revenue
                            </div>
                            <div className="flex items-baseline gap-1 flex-wrap">
                                {hasAccess ? (
                                    <>
                                        <span className={`text-sm ${getColorClass(revBeat, revMiss)}`} style={{ color: revBeat ? '#22c55e' : revMiss ? '#ef4444' : undefined }}>
                                            {formatCurrency(item.revenueActual)}
                                        </span>
                                        {item.revenueEstimated !== null && (
                                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                / {formatCurrency(item.revenueEstimated)}
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex items-center gap-1 text-gray-400">
                                        <Lock size={12} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Transcript Button */}
                        <button
                            onClick={() => {
                                if (hasAccess) {
                                    handleShowTranscript();
                                } else {
                                    window.dispatchEvent(new CustomEvent('sf-open-auth'));
                                }
                            }}
                            disabled={isLoadingTranscript}
                            className={`mt-1 w-full py-1 px-2 rounded text-[10px] font-medium flex items-center justify-center gap-1 transition-colors ${isDark
                                ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 disabled:opacity-50'
                                : 'bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50'
                                }`}
                        >
                            {isLoadingTranscript ? (
                                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : hasAccess ? (
                                <FileText size={10} />
                            ) : (
                                <Lock size={10} />
                            )}
                            {isLoadingTranscript ? 'Loading...' : hasAccess ? 'Transcript' : ''}
                        </button>
                    </div>
                </div>
            </div>

            {showTranscript && (
                <SmartTranscript
                    rawText={transcriptData?.content || ''}
                    companyName={item.companyName}
                    quarter={transcriptData?.quarter ? `Q${transcriptData.quarter}` : 'Q3'}
                    year={transcriptData?.year?.toString() || new Date().getFullYear().toString()}
                    sector={item.sector}
                    onClose={() => setShowTranscript(false)}
                />
            )}
        </>
    );
};
